import { useState, useMemo } from "react";
import { AppointmentData } from "@/services/appointment-service";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Edit2, ArrowUpDown, ChevronUp, ChevronDown, Calendar, Send } from "lucide-react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { statusColors, statusLabels } from "@/utils/appointmentStatus";
import { formatCPF, formatPhone } from "@/utils/formatters";

interface AppointmentsTableProps {
  appointments: AppointmentData[];
  onSendRebookingInvite?: (appointment: AppointmentData) => void;
  onRebooking?: (appointment: AppointmentData) => void;
  onEdit?: (appointment: AppointmentData) => void;
  hideClinic?: boolean;
  hideDentist?: boolean;
  hideCpf?: boolean;
  hideCellphone?: boolean;
}

type SortKeys = keyof AppointmentData | 'full_date';
type SortConfig = { key: SortKeys; direction: 'asc' | 'desc' } | null;

export function AppointmentsTable({
  appointments,
  onSendRebookingInvite,
  onRebooking,
  onEdit,
  hideClinic,
  hideDentist,
  hideCpf,
  hideCellphone }: AppointmentsTableProps) {
  
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'full_date', direction: 'asc' });

  const sortedAppointments = useMemo(() => {
    let sortableItems = [...appointments];
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        let aValue: any;
        let bValue: any;

        if (sortConfig.key === 'full_date') {
          aValue = new Date(`${a.slot_date}T${a.slot_time}`).getTime();
          bValue = new Date(`${b.slot_date}T${b.slot_time}`).getTime();
        } else {
          aValue = a[sortConfig.key as keyof AppointmentData] ?? '';
          bValue = b[sortConfig.key as keyof AppointmentData] ?? '';
          if (typeof aValue === 'string') aValue = aValue.toLowerCase();
          if (typeof bValue === 'string') bValue = bValue.toLowerCase();
        }

        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return sortableItems;
  }, [appointments, sortConfig]);

  const requestSort = (key: SortKeys) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  const SortIcon = ({ column }: { column: SortKeys }) => {
    if (sortConfig?.key !== column) return <ArrowUpDown className="ml-2 h-3 w-3 opacity-30" />;
    return sortConfig.direction === 'asc' 
      ? <ChevronUp className="ml-2 h-3 w-3 text-primary font-bold" /> 
      : <ChevronDown className="ml-2 h-3 w-3 text-primary font-bold" />;
  };

  return (
    <div className="rounded-lg border border-border overflow-hidden bg-card">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50 hover:bg-muted/50">
              <TableHead className="th cursor-pointer select-none" onClick={() => requestSort('appointment_number')}>
                <div className="flex items-center">Nº Atend. <SortIcon column="appointment_number" /></div>
              </TableHead>
              <TableHead className="th min-w-[130px] cursor-pointer select-none" onClick={() => requestSort('full_date')}>
                <div className="flex items-center">Horário <SortIcon column="full_date" /></div>
              </TableHead>
              <TableHead className="th cursor-pointer select-none" onClick={() => requestSort('patient_name')}>
                <div className="flex items-center">Paciente <SortIcon column="patient_name" /></div>
              </TableHead>
              {!hideCpf && <TableHead className="th">CPF</TableHead>}
              {!hideCellphone && <TableHead className="th">Celular</TableHead>}
              {!hideDentist && <TableHead className="th">Dentista</TableHead>}
              <TableHead className="th">Status</TableHead>
              <TableHead className="th text-center">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedAppointments.map((appointment) => (
              <TableRow key={appointment.id} className="hover:bg-muted/30 transition-colors">
                <TableCell className="td font-mono">{appointment.appointment_number}</TableCell>
                <TableCell className="td min-w-[130px] font-medium">
                  {appointment.slot_date ? (() => {
                    const [year, month, day] = appointment.slot_date.split('-');
                    const dateFormatted = `${day}/${month}/${year.slice(-2)}`;
                    const startTime = appointment.slot_time.substring(0, 5);

                    // Se a duração for maior que 30 (ex: 60), adiciona o (1h) em vermelho
                    if (appointment.duration_minutes && appointment.duration_minutes > 30) {
                      // Calculamos as horas de forma dinâmica (ex: 60min -> 1h, 90min -> 1.5h)
                      const durationHours = appointment.duration_minutes / 60;
                      
                      return (
                        <div className="flex items-center gap-1">
                          <span>{dateFormatted} {startTime}</span>
                          <span className="text-red-500 font-bold">({durationHours}h)</span>
                        </div>
                      );
                    }

                    // Retorno padrão para 30 minutos
                    return `${dateFormatted} ${startTime}`;
                  })() : '—'}
                </TableCell>
                <TableCell className="py-1.5 px-2 max-w-[220px]">
                  <div className="flex flex-col">
                    <span className="text-xs font-semibold truncate">{appointment.patient_name}</span>
                    <span className="text-[10px] text-muted-foreground uppercase">{appointment.appointment_dental_insurance_name}</span>
                  </div>
                </TableCell>
                {!hideCpf && <TableCell className="td">{formatCPF(appointment.patient_cpf)}</TableCell>}
                {!hideCellphone && <TableCell className="td">{formatPhone(appointment.patient_cellphone)}</TableCell>}
                {!hideDentist && <TableCell className="td">{appointment.dentist_name}</TableCell>}
                <TableCell className="td">
                  <Badge variant="outline" className={`text-[10px] ${statusColors[appointment.appointment_status]} border-current`}>
                    {statusLabels[appointment.appointment_status]}
                    {appointment.confirmed && <span className="ml-1">✓</span>}
                  </Badge>
                </TableCell>
                <TableCell className="td text-center">
                  <div className="flex items-center justify-center gap-1">
                    {/* Convite de Reagendamento — só para booked (paciente escolhe novo horário no WhatsApp) */}
                    {onSendRebookingInvite && (
                      <Button
                        variant="ghost" size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-green-700 hover:bg-green-50"
                        disabled={appointment.appointment_status !== 'booked'}
                        onClick={() => onSendRebookingInvite(appointment)}
                        title="Convite de Reagendamento"
                      >
                        <Send className="h-4 w-4" />
                      </Button>
                    )}

                    {/* Reagendar — atendente escolhe horário direto no wizard */}
                    {onRebooking && (
                      <Button
                        variant="ghost" size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-blue-700 hover:bg-blue-50"
                        disabled={appointment.appointment_status !== 'booked'}
                        onClick={() => onRebooking(appointment)}
                        title="Reagendar consulta"
                      >
                        <Calendar className="h-4 w-4" />
                      </Button>
                    )}

                    {onEdit && (
                      <Button
                        size="icon" variant="ghost"
                        className="h-8 w-8 text-muted-foreground"
                        onClick={() => onEdit(appointment)}
                        title="Editar agendamento"
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}